import { useState, useEffect, useCallback, useRef } from "react";
import { evolveBadges } from "@/lib/badges";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import api, { BACKEND_URL } from "@/lib/api";
import { getMusicUrl, getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import AudioPlayer from "@/components/AudioPlayer";
import { PlayButton } from "@/components/player/PlayButton";
import { SinglesLockBadge, isSinglesLocked, isSinglesForSale } from "@/components/singles/SinglesLockBadge";
import SharedNavigation from "@/components/SharedNavigation";
import BackButton from "@/components/BackButton";
import GiftPicker from "@/components/GiftPicker";
import TopSupportersModal from "@/components/TopSupportersModal";
import SupportersPreviewRow from "@/components/SupportersPreviewRow";
import AITrackFeedback from "@/components/AITrackFeedback";
import AIPromoGenerator from "@/components/AIPromoGenerator";
import CollabSuggestions from "@/components/CollabSuggestions";
import ShareToCommunityDialog from "@/components/ShareToCommunityDialog";
import TrackReactionBar from "@/components/TrackReactionBar";
import MentionTextarea from "@/components/MentionTextarea";
import { MilestoneBadgeLarge, MilestoneCelebration, MILESTONES } from "@/components/MilestoneBadge";
import { FeaturedConfettiBurst, useFeaturedArrival } from "@/components/FeaturedCelebration";
import TrackStatusChips from "@/components/TrackStatusChips";
import TrackContestBadge from "@/components/contests/TrackContestBadge";
import ReviewHistoryModal from "@/components/ReviewHistoryModal";
import NowFeaturedShareModal from "@/components/NowFeaturedShareModal";
import TrackShareModal from "@/components/share/TrackShareModal";
import { useShareVisibility } from "@/lib/useShareVisibility";
import { fireTutorialTrigger } from "@/lib/tutorialTriggers";
import { useTutorial } from "@/components/tutorial/TutorialProvider";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import { toast } from "sonner";
import { stashReferral } from "@/lib/referralStash";

// Phase 13 (Share Ecosystem Unification): Build the SAME canonical
// decorated summary the backend OG endpoint uses so JS-aware crawlers
// (X, Discord, modern Slack) read identical numbers. Also keeps the
// in-browser tab title fresh.
const fmtCompact = (n) => {
  const x = Number(n || 0);
  if (x < 1000) return String(x);
  if (x < 1_000_000) {
    const v = x / 1000;
    return v < 10 ? `${v.toFixed(1)}K` : `${Math.floor(v)}K`;
  }
  const v = x / 1_000_000;
  return v < 10 ? `${v.toFixed(1)}M` : `${Math.floor(v)}M`;
};

const updateMetaTags = ({ title, description, imageUrl, profilePhoto, momentumBadge,
                          displayPlays, reach, impressions }) => {
  document.title = title;

  const upsert = (key, value, attr = "property") => {
    if (!value) return;
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", String(value));
  };

  upsert("og:type", "music.song");
  upsert("og:title", title);
  upsert("og:description", description);
  upsert("og:image", imageUrl);
  upsert("og:image:secure_url", imageUrl);
  upsert("og:image:alt", title);
  if (profilePhoto && profilePhoto !== imageUrl) {
    // Slack/Telegram render multiple og:image entries.
    upsert("og:image:secondary", profilePhoto);
  }
  upsert("og:url", typeof window !== "undefined" ? window.location.href : "");
  upsert("og:site_name", "RGM Studio");
  if (momentumBadge) upsert("rgm:momentum_badge", momentumBadge);
  upsert("rgm:display_plays", String(Number(displayPlays || 0)));
  upsert("rgm:reach", String(Number(reach || 0)));
  upsert("rgm:impressions", String(Number(impressions || 0)));

  upsert("twitter:card", "summary_large_image", "name");
  upsert("twitter:title", title, "name");
  upsert("twitter:description", description, "name");
  upsert("twitter:image", imageUrl, "name");
  upsert("twitter:image:alt", title, "name");
  upsert("twitter:label1", "Plays", "name");
  upsert("twitter:data1", Number(displayPlays || 0).toLocaleString(), "name");
  upsert("twitter:label2", reach > 0 ? "Reach" : "Status", "name");
  upsert("twitter:data2",
    reach > 0 ? Number(reach).toLocaleString() : (momentumBadge || "On RGM Studio"),
    "name",
  );

  upsert("description", description, "name");
};

// Badge component
const Badge = ({ type }) => {
  const badges = {
    founder: { label: "Founder", icon: "👑", className: "bg-gradient-to-r from-yellow-400 to-amber-600 text-black" },
    founding_artist: { label: "Founding Artist", icon: "⭐", className: "bg-gradient-to-r from-purple-500 to-pink-600 text-white" },
    artist: { label: "Artist", icon: "🎤", className: "bg-gray-700 text-white" },
    spotlight_winner: { label: "Spotlight", icon: "🏆", className: "bg-gradient-to-r from-green-500 to-emerald-600 text-white" },
    admin: { label: "Admin", icon: "⚡", className: "bg-purple-600 text-white" }
  };
  const badge = badges[type];
  if (!badge) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${badge.className}`}>
      <span>{badge.icon}</span>
      {badge.label}
    </span>
  );
};

const TrackPage = () => {
  const { submission_id } = useParams();

  // Phase 9.6.1 — Celebration Push: fire confetti burst when user arrives
  // from the in-app featured toast (`?from=featured`). Also pings the
  // backend click counter so artists see the engagement.
  const showFeaturedBurst = useFeaturedArrival(typeof window !== "undefined" ? window.location.search : "");
  useEffect(() => {
    if (!submission_id) return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("from") === "featured") {
      api.post(`/feature/${submission_id}/track-click`).catch(() => { /* ignore */ });
    }
  }, [submission_id]);

  // Phase 5 polish: capture ?ref= from shared track URLs so that if the
  // visitor later signs up (possibly on another page), the referrer still
  // gets credited. LoginPage reads this sessionStorage key as a fallback.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const ref = qs.get('ref');
    if (ref) {
      stashReferral(ref);
    }
  }, []);
  const [trackData, setTrackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [playCountIncremented, setPlayCountIncremented] = useState(false);
  const [celebratedMilestone, setCelebratedMilestone] = useState(null);  // Phase 6 Final: milestone celebration overlay
  const [currentUser, setCurrentUser] = useState(null);
  const { startSection } = useTutorial();
  const [canEdit, setCanEdit] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentMentions, setCommentMentions] = useState([]);
  const [replyMentions, setReplyMentions] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  // Phase 3.2: comment replies + reactions
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [reactionPickerFor, setReactionPickerFor] = useState(null); // comment_id whose picker is open
  const [isAdmin, setIsAdmin] = useState(false);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [showPlaylistDropdown, setShowPlaylistDropdown] = useState(false);
  const [addingToPlaylist, setAddingToPlaylist] = useState(false);
  const playlistDropdownRef = useRef(null);
  // #9 Unclaimed milestones for backfill Claim & Share
  const [unclaimedMilestones, setUnclaimedMilestones] = useState([]);
  const [claimingThreshold, setClaimingThreshold] = useState(null);
  // Share-to-community dialog (crews / groups / clubs)
  const [shareCommunityOpen, setShareCommunityOpen] = useState(false);
  // Phase 20.3 — Track Repost
  const [reposted, setReposted] = useState(false);
  const [repostBusy, setRepostBusy] = useState(false);
  // Phase 7.1 Milestone C — Top Supporters
  const [supportersOpen, setSupportersOpen] = useState(false);
  const [supportersRefreshKey, setSupportersRefreshKey] = useState(0);
  // Phase 9.7.15 — A&R Review History modal
  const [reviewHistoryOpen, setReviewHistoryOpen] = useState(false);
  // Phase 9.7.20 — Now Featured share modal
  const [nowFeaturedShareOpen, setNowFeaturedShareOpen] = useState(false);
  const [trackShareOpen, setTrackShareOpen] = useState(false);  // iter_189 unified share modal

  // Close playlist dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (playlistDropdownRef.current && !playlistDropdownRef.current.contains(event.target)) {
        setShowPlaylistDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Phase 15.2 #6 — first-time featured nudge for the owner.
  // Fires once (idempotent via local flag) when an artist lands on
  // their own track and it has reached featured/spotlight status.
  useEffect(() => {
    const sub = trackData?.submission;
    if (!sub || !currentUser) return;
    if (sub.user_id !== currentUser.user_id) return;
    if (sub.status !== "featured" && sub.status !== "spotlight") return;
    fireTutorialTrigger("first_feature", { openTutorial: startSection });
  }, [trackData, currentUser, startSection]);

  const fetchUnclaimedMilestones = useCallback(async () => {
    try {
      const r = await api.get(`/track/${submission_id}/unclaimed-milestones`);
      setUnclaimedMilestones(r.data?.unclaimed || []);
    } catch (_e) {
      // non-blocking
    }
  }, [submission_id]);

  const handleClaimMilestone = async (threshold) => {
    setClaimingThreshold(threshold);
    try {
      await api.post(`/track/${submission_id}/claim-milestone/${threshold}`);
      const full = (MILESTONES || []).find((m) => m.threshold === threshold);
      if (full) setCelebratedMilestone(full);
      setUnclaimedMilestones((prev) => prev.filter((m) => m.threshold !== threshold));
      toast.success(`🎉 ${threshold.toLocaleString()} plays milestone claimed!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim milestone");
    } finally {
      setClaimingThreshold(null);
    }
  };

  const fetchTrack = useCallback(async () => {
    try {
      const response = await api.get(`/track/${submission_id}`);
      setTrackData(response.data);
      setLikeCount(response.data.submission?.likes?.length || 0);
      
      // Update meta tags for social sharing (canonical decorated data).
      const submission = response.data.submission;
      const artist = response.data.artist;
      if (submission) {
        const trackTitle = submission.track_title || 'Untitled Track';
        const artistName = artist?.artist_name || submission.artist_name || 'Unknown Artist';
        const coverUrl = getCoverUrl(submission, 'cover') || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200&h=630&fit=crop';
        const profilePhoto = getProfilePhotoUrl({
          profile_photo: artist?.profile_photo,
          picture: artist?.picture,
        });
        const displayPlays = Number(
          submission.display_plays ??
          ((submission.play_count || 0) + (submission.promotional_plays || 0))
        );
        const reach = Number(
          (submission.boost_applied || 0) + (submission.floor_applied || 0)
        );
        const impressions = reach * 8;
        const momentumBadge = submission.momentum_badge ||
          ((submission.status === 'featured' || submission.status === 'spotlight')
            ? '🔥 In Rotation' : '');

        const parts = [];
        if (momentumBadge) parts.push(momentumBadge);
        if (displayPlays > 0) parts.push(`▶ ${fmtCompact(displayPlays)} plays`);
        if (reach > 0) parts.push(`🏆 ${fmtCompact(reach)} reach`);
        if (impressions > 0) parts.push(`👁 ${fmtCompact(impressions)} impressions`);
        const summary = parts.length ? parts.join(' · ') : '🎶 Listen on RGM Studio';
        const longDesc = (submission.description || '').trim();
        const description = longDesc
          ? `${summary} — ${longDesc.slice(0, 160)}`
          : `${summary} on RGM Studio. Listen to “${trackTitle}” by ${artistName}.`;

        updateMetaTags({
          title: `🎵 ${trackTitle} — ${artistName} | RGM Studio`,
          description,
          imageUrl: coverUrl,
          profilePhoto,
          momentumBadge,
          displayPlays,
          reach,
          impressions,
        });
      }
    } catch (error) {
      console.error("Failed to fetch track:", error);
    } finally {
      setLoading(false);
    }
  }, [submission_id]);

  const checkCurrentUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      setIsAdmin(response.data.role === 'admin');
      // Check if user can edit this track
      try {
        const canEditResponse = await api.get(`/track/${submission_id}/can-edit`);
        setCanEdit(canEditResponse.data.can_edit);
      } catch (e) {
        setCanEdit(false);
      }
      // Check like status
      try {
        const likeResponse = await api.get(`/track/${submission_id}/like-status`);
        setLiked(likeResponse.data.liked);
        setLikeCount(likeResponse.data.like_count);
      } catch (e) {
        // Ignore - will use default from track data
      }
      // Fetch user's playlists for "Add to Playlist" feature
      try {
        const playlistsResponse = await api.get('/playlists/my');
        setUserPlaylists(playlistsResponse.data.playlists || []);
      } catch (e) {
        // No playlists or not logged in
      }
    } catch (error) {
      // Not logged in
      setCurrentUser(null);
      setCanEdit(false);
      setIsAdmin(false);
    }
  }, [submission_id]);

  const handleAddToPlaylist = async (playlistId) => {
    setAddingToPlaylist(true);
    try {
      await api.post(`/playlists/${playlistId}/tracks/${submission_id}`);
      toast.success("Track added to playlist!");
      setShowPlaylistDropdown(false);
    } catch (error) {
      if (error.response?.data?.detail?.includes("already")) {
        toast.info("Track is already in this playlist");
      } else {
        toast.error("Failed to add track to playlist");
      }
    } finally {
      setAddingToPlaylist(false);
    }
  };

  const fetchComments = useCallback(async () => {
    try {
      const response = await api.get(`/track/${submission_id}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    }
  }, [submission_id]);

  // Kick off the data-load side-effects. Relocated below the four
  // `useCallback` declarations it depends on to eliminate the
  // temporal-dead-zone crash ("Cannot access 'fetchTrack' before
  // initialization") that occurred when the dependency array was
  // evaluated during render before the callbacks had been assigned.
  // Behavior is otherwise identical: same triggers, same deps, same
  // callback identities.
  useEffect(() => {
    fetchTrack();
    fetchComments();
    checkCurrentUser();
    fetchUnclaimedMilestones();
  }, [submission_id, fetchTrack, fetchComments, checkCurrentUser, fetchUnclaimedMilestones]);

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    if (!currentUser) {
      toast.error("Please log in to comment");
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await api.post(`/track/${submission_id}/comments`, {
        content: commentText,
        mention_user_ids: commentMentions,
      });
      // Append new comment (list is sorted oldest → newest)
      setComments(prev => [...prev, response.data.comment]);
      setCommentText("");
      setCommentMentions([]);
      toast.success("Comment posted!");
    } catch (error) {
      console.error("Failed to post comment:", error);
      toast.error(error.response?.data?.detail || "Failed to post comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  // Phase 3.2: post a reply to an existing comment
  const handleSubmitReply = async (parentId) => {
    if (!replyText.trim()) return;
    if (!currentUser) {
      toast.error("Please log in to reply");
      return;
    }
    setSubmittingReply(true);
    try {
      const response = await api.post(`/track/${submission_id}/comments`, {
        content: replyText,
        parent_comment_id: parentId,
        mention_user_ids: replyMentions,
      });
      setComments(prev => [...prev, response.data.comment]);
      setReplyMentions([]);
      setReplyText("");
      setReplyTargetId(null);
      toast.success("Reply posted!");
    } catch (error) {
      console.error("Failed to post reply:", error);
      toast.error(error.response?.data?.detail || "Failed to post reply");
    } finally {
      setSubmittingReply(false);
    }
  };

  // Phase 3.2: toggle an emoji reaction on a comment
  const handleToggleReaction = async (commentId, emoji) => {
    if (!currentUser) {
      toast.error("Please log in to react");
      return;
    }
    try {
      const response = await api.post(
        `/track/${submission_id}/comments/${commentId}/react`,
        { emoji }
      );
      // Update local state optimistically
      setComments(prev => prev.map(c =>
        c.comment_id === commentId
          ? { ...c, reactions: response.data.reactions || {} }
          : c
      ));
      setReactionPickerFor(null);
      // Phase 5 polish: safety-net refetch ensures reactions are always in sync
      // across parents, replies, and multi-tab scenarios (and catches any
      // server-side cleanup we didn't mirror locally).
      fetchComments().catch(() => {});
    } catch (error) {
      console.error("Failed to toggle reaction:", error);
      toast.error(error.response?.data?.detail || "Failed to react");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/track/${submission_id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      toast.success("Comment deleted");
    } catch (error) {
      console.error("Failed to delete comment:", error);
      toast.error(error.response?.data?.detail || "Failed to delete comment");
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Callback for when 10+ seconds of playback occurs
  const handlePlayCountIncrement = useCallback(async (trackId) => {
    if (playCountIncremented) return;

    try {
      const res = await api.post(`/track/${trackId}/play-count`);
      setPlayCountIncremented(true);
      const newCount = res?.data?.play_count;
      // Update local play count
      setTrackData(prev => ({
        ...prev,
        submission: {
          ...prev.submission,
          play_count: typeof newCount === 'number' ? newCount : ((prev.submission?.play_count || 0) + 1)
        }
      }));

      // Phase 6 Final: milestone celebration. Backend returns `milestone_achieved`
      // when this play tipped the track over a new threshold. Fire confetti +
      // the "Share your win?" modal. Guarded per-device so re-playing the same
      // track doesn't re-celebrate.
      const ms = res?.data?.milestone_achieved;
      if (ms && ms.threshold) {
        const storageKey = `rgm_milestone_celebrated_${trackId}_${ms.threshold}`;
        try {
          if (!window.localStorage.getItem(storageKey)) {
            const full = (MILESTONES || []).find((m) => m.threshold === ms.threshold);
            if (full) {
              setCelebratedMilestone(full);
              window.localStorage.setItem(storageKey, '1');
            }
          }
        } catch (_e) {
          // localStorage unavailable (Safari private mode etc) — still celebrate
          const full = (MILESTONES || []).find((m) => m.threshold === ms.threshold);
          if (full) setCelebratedMilestone(full);
        }
      }
    } catch (error) {
      console.error("Failed to increment play count:", error);
    }
  }, [playCountIncremented]);

  const handleLike = async () => {
    if (!currentUser) {
      toast.error("Please log in to like tracks");
      return;
    }
    
    try {
      const response = await api.post(`/track/${submission_id}/like`);
      setLiked(response.data.liked);
      setLikeCount(response.data.like_count);
    } catch (error) {
      console.error("Failed to like track:", error);
      toast.error("Failed to like track");
    }
  };

  // Phase 20.3 — Track Repost: load my repost state for this track
  useEffect(() => {
    if (!submission_id || !currentUser) { setReposted(false); return; }
    let alive = true;
    api.get(`/track/${submission_id}/repost`)
      .then((r) => { if (alive) setReposted(!!r.data?.reposted); })
      .catch(() => {});
    return () => { alive = false; };
  }, [submission_id, currentUser]);

  const handleRepost = async () => {
    if (!currentUser) { toast.error("Sign in to repost"); return; }
    if (repostBusy) return;
    setRepostBusy(true);
    try {
      const r = await api.post(`/track/${submission_id}/repost`);
      const isReposted = !!r.data?.reposted;
      setReposted(isReposted);
      toast.success(isReposted
        ? "🔀 Added to RGM Shuffle"
        : "Repost removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not repost");
    } finally {
      setRepostBusy(false);
    }
  };

  const { canShare } = useShareVisibility();

  const handleShare = async () => {
    // iter_189 — Unified Share System. The dedicated modal renders the
    // branded 1080×1350 card (cover + artist + title + URL + RGM logo)
    // and exposes the full channel row (Native/Copy/Download/WhatsApp/
    // X/FB/Threads/Instagram/Messenger/SMS/Email). The old URL-only
    // fast path is preserved as a fallback when the modal fails to
    // open (feature-flag off / no submission).
    api.post(`/track/${submission_id}/share`).catch(() => {});
    setTrackShareOpen(true);
  };

  // Phase 6 Part 3: Milestone share cards — shareable tiers with dynamic OG
  // preview. Top 3 are the "big" cards (Certified / Global / Platinum), but we
  // surface them all so the artist can celebrate every crossing.
  const MILESTONE_SHARE_TIERS = [
    { threshold: 10000, emoji: "👑", name: "Platinum Track", accent: "from-amber-300 via-yellow-400 to-amber-500" },
    { threshold: 7500,  emoji: "⚡", name: "Viral Track",    accent: "from-fuchsia-500 to-violet-500" },
    { threshold: 5000,  emoji: "🌍", name: "Global Track",   accent: "from-emerald-400 to-teal-500" },
    { threshold: 2500,  emoji: "💎", name: "Breakout Track", accent: "from-cyan-400 to-blue-500" },
    { threshold: 1000,  emoji: "🏆", name: "RGM Certified",  accent: "from-purple-500 to-pink-500" },
    { threshold: 500,   emoji: "📣", name: "Buzzing Track",  accent: "from-pink-500 to-rose-500" },
    { threshold: 100,   emoji: "⭐", name: "Trending Track", accent: "from-yellow-400 to-amber-500" },
  ];

  const topMilestoneReached = () => {
    const count = trackData?.submission?.play_count || 0;
    // Pick the highest threshold crossed
    for (const m of MILESTONE_SHARE_TIERS) {
      if (count >= m.threshold) return m;
    }
    return null;
  };

  const handleShareMilestone = async (threshold) => {
    if (!threshold) return;
    const tier = MILESTONE_SHARE_TIERS.find((m) => m.threshold === threshold);
    const sharerRef = currentUser?.referral_code || currentUser?.artist_name || currentUser?.name;
    let shareUrl = `${BACKEND_URL}/api/share/milestone/${submission_id}/${threshold}`;
    if (sharerRef) shareUrl += `?ref=${encodeURIComponent(sharerRef)}`;
    // Fire-and-forget share counter
    api.post(`/track/${submission_id}/share`).catch(() => {});
    const title = tier
      ? `${tier.emoji} ${getArtistName()} just hit ${threshold.toLocaleString()} plays!`
      : `Milestone on RGM Studio`;
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: `"${trackData?.submission?.track_title}" just earned the ${tier?.name || 'milestone'} badge on RGM Studio.`,
          url: shareUrl,
        });
      } catch (_e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Milestone link copied — paste to share!');
      } catch (_e) {
        toast.error('Could not copy link');
      }
    }
  };

  const getArtistName = () => {
    return trackData?.artist?.artist_name || trackData?.submission?.artist_name || 'Unknown Artist';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading track...</p>
        </div>
      </div>
    );
  }

  if (!trackData?.submission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <p className="text-2xl text-white mb-4">Track not found</p>
          <Link to="/">
            <Button className="bg-purple-600">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { submission, artist } = trackData;
  
  // Get media URLs (supports both Cloudinary and local storage)
  const coverImageUrl = getCoverUrl(submission);
  const musicFileUrl = getMusicUrl(submission);
  const artistPhotoUrl = getProfilePhotoUrl(artist);

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="track-page">
      {/* Shared Header */}
      <SharedNavigation currentUser={currentUser} />

      {/* Phase 9.6.1 — Featured Celebration confetti */}
      {showFeaturedBurst && <FeaturedConfettiBurst />}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-20">
        <BackButton fallback="/discover" testId="track-back-button" />
        {/* Phase 14.2 — Share Your Feature CTA only. Status chips moved
            BELOW the title (see TrackStatusChips) so cover art stays clean. */}
        {submission && (submission.status === "featured" || submission.status === "spotlight") && currentUser && canShare("tracks") && (
          currentUser.user_id === submission.user_id ||
          currentUser.role === "admin" ||
          currentUser.role === "ar"
        ) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => setNowFeaturedShareOpen(true)}
              className="text-xs px-3 py-2 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold flex items-center gap-1.5 shadow-lg shadow-amber-500/30"
              data-testid="share-your-feature-btn"
            >
              <span>🎖️</span>
              <span>Share Your Feature</span>
            </button>
          </div>
        )}
      </div>

      {/* Phase 6 Final: Milestone celebration overlay */}
      <MilestoneCelebration
        milestone={celebratedMilestone}
        onClose={() => setCelebratedMilestone(null)}
        onShare={canShare("tracks") ? (threshold) => handleShareMilestone(threshold) : undefined}
      />

      {/* Share to crew / group / club — audio keeps mounted behind the overlay */}
      <ShareToCommunityDialog
        open={shareCommunityOpen}
        onClose={() => setShareCommunityOpen(false)}
        submissionId={submission_id}
        trackTitle={submission?.track_title}
      />

      {/* Phase 7.1 Milestone C — Top Supporters modal lives at the top of
          the TrackPage tree (sibling of the AudioPlayer subtree) so opening
          it never causes a re-render that would unmount playback. */}
      <TopSupportersModal
        open={supportersOpen}
        onClose={() => setSupportersOpen(false)}
        kind="track"
        targetId={submission_id}
        title={trackData?.submission?.track_title || "Track"}
        refreshKey={supportersRefreshKey}
      />

      {/* Track Content */}
      <div className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Track Card */}
          <Card className="overflow-hidden bg-gradient-to-br from-purple-900/30 via-gray-900/80 to-black border-purple-500/30">
            {/* Cover Art Section — Phase 14.2: kept clean of overlay
                banners. Status chips render BELOW the title via
                <TrackStatusChips/> so artwork stays visually intact. */}
            <div className="relative">
              <div className="aspect-square max-h-96 w-full md:w-96 mx-auto">
                <img
                  src={coverImageUrl}
                  alt={submission.track_title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop'; }}
                />
              </div>
            </div>

            {/* Track Info */}
            <div className="p-6 md:p-8">
              {/* Title and Artist */}
              <h1 className="text-3xl md:text-4xl font-black text-white mb-2" data-testid="track-title">
                {submission.track_title || 'Untitled'}
              </h1>
              
              <div className="flex items-center gap-3 mb-4">
                <Link to={`/artist/${submission.user_id}`} className="flex items-center gap-2 hover:underline">
                  {artist && (
                    <img
                      src={artistPhotoUrl}
                      alt={getArtistName()}
                      className="w-8 h-8 rounded-full"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop'; }}
                    />
                  )}
                  <span className="text-purple-400 font-semibold text-lg">{getArtistName()}</span>
                </Link>
                {evolveBadges(artist?.badges)?.map(badge => (
                  <Badge key={badge} type={badge} />
                ))}
              </div>

              {/* Phase 3.2: Featuring artists */}
              {Array.isArray(submission.featured_artists_enriched) && submission.featured_artists_enriched.length > 0 && (
                <div className="flex items-center flex-wrap gap-2 mb-4" data-testid="featuring-row">
                  <span className="text-gray-400 text-sm">Featuring</span>
                  {submission.featured_artists_enriched.map((f) => (
                    <Link
                      key={f.user_id}
                      to={`/artist/${f.user_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-200 text-xs hover:bg-purple-500/25 transition-all"
                      data-testid={`featured-artist-${f.user_id}`}
                    >
                      {(f.profile_photo || f.picture) && (
                        <img
                          src={getProfilePhotoUrl({ profile_photo: f.profile_photo, picture: f.picture })}
                          alt={f.artist_name}
                          className="w-4 h-4 rounded-full"
                        />
                      )}
                      <span>{f.artist_name}</span>
                    </Link>
                  ))}
                </div>
              )}

              {/* Phase 14.2 — Compact status chips row (Spotlight /
                  Feature Boost / Surge / Founder Showcase / Milestone).
                  Replaces five separate full-width overlay banners. */}
              <TrackStatusChips submission={submission} className="mb-4" />

              {/* Phase 21.4 Batch 7 — 🏆 Track-page contest participation badge.
                  Renders only when this track is entered in a live contest.
                  Silent (returns null) if not entered or flag OFF. */}
              <TrackContestBadge submissionId={submission?.submission_id} artistUserId={submission?.user_id} />

              {/* Genre & Stats */}
              <div className="flex items-center gap-4 mb-3">
                <span className="px-3 py-1 bg-purple-900/50 border border-purple-500/30 rounded-full text-purple-300 text-sm">
                  {submission.genre}
                </span>
                <span
                  className="text-gray-500 text-sm flex items-center gap-1"
                  title="Total plays (real + promotional)"
                  data-testid="track-display-plays"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {(submission.display_plays
                    ?? ((submission.play_count || 0) + (submission.promotional_plays || 0))
                  ).toLocaleString()} plays
                </span>
                {/* Phase 20.2 — Promo Plays Breakdown chip */}
                {(submission.promotional_plays || 0) > 0 && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-200 text-xs font-bold"
                    title={`Organic: ${(submission.play_count || 0).toLocaleString()} · Promotional: ${(submission.promotional_plays || 0).toLocaleString()}`}
                    data-testid="track-promo-breakdown"
                  >
                    📣 +{(submission.promotional_plays || 0).toLocaleString()} promo
                  </span>
                )}
                {submission.score > 0 && (
                  <span className={`font-bold ${submission.score >= 45 ? 'text-green-400' : submission.score >= 30 ? 'text-yellow-400' : 'text-gray-400'}`}>
                    Score: {submission.score}/60
                  </span>
                )}
              </div>

              {/* Phase 3.2 Final: engagement metrics row (real, not fake likes) */}
              {(() => {
                const eng = submission.engagement || {};
                if (!eng.feature_reach && !eng.playlist_adds && !eng.shares) return null;
                const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n || 0}`);
                return (
                  <div
                    className="mb-6 flex flex-wrap items-center gap-3 text-sm"
                    data-testid="engagement-metrics"
                  >
                    {eng.feature_reach > 0 && (
                      <span className="text-orange-300">🔥 Featured Reach: {fmt(eng.feature_reach)}</span>
                    )}
                    {eng.new_listeners > 0 && (
                      <span className="text-purple-300">• {fmt(eng.new_listeners)} listeners</span>
                    )}
                    <span className="text-gray-400">• {eng.playlist_adds || 0} playlist adds</span>
                    <span className="text-gray-400">• {eng.shares || 0} shares</span>
                    {typeof submission.ai_feedback?.overall === "number" && currentUser && (
                      currentUser.user_id === submission.user_id ||
                      currentUser.role === 'admin' ||
                      currentUser.role === 'ar'
                    ) && (
                      <span
                        className="px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-200"
                        data-testid="ai-score-chip"
                      >
                        ⭐ AI Score: {submission.ai_feedback.overall}
                      </span>
                    )}
                    {eng.spotlight_position && (
                      <span
                        className="px-2 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-200"
                        data-testid="spotlight-rank-chip"
                      >
                        🏆 Rank: #{eng.spotlight_position} This Week
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Singles Store — For-Sale / Preview lock hero.
                  Renders only when the submission is actively listed. */}
              {isSinglesForSale(submission) && (
                <SinglesLockBadge
                  track={submission}
                  variant="hero"
                  className="mb-4"
                  testIdSuffix="track-page"
                />
              )}

              {/* Embedded Audio Player.
                  Singles Store: when locked, swap the master URL for the
                  server-generated 30-45s preview endpoint. The backend
                  has already redacted `music_url` server-side; this is
                  the ONLY safe playback surface for unauthorized viewers. */}
              <div className="mb-6">
                <AudioPlayer
                  src={isSinglesLocked(submission) && submission.singles_preview_url
                    ? `${BACKEND_URL}${submission.singles_preview_url.startsWith('/') ? '' : '/'}${submission.singles_preview_url}`
                    : musicFileUrl}
                  trackId={submission.submission_id}
                  trackTitle={submission.track_title || 'Untitled'}
                  artistName={getArtistName()}
                  coverArt={submission.cover_url || null}
                  onPlayCountIncrement={isSinglesLocked(submission) ? null : handlePlayCountIncrement}
                />
                {/* Phase 15.6J — Listen-while-you-browse handoff to global mini player */}
                <div className="mt-2 flex items-center gap-2">
                  <PlayButton
                    track={{
                      submission_id: submission.submission_id,
                      track_title: submission.track_title,
                      artist_name: getArtistName(),
                      user_id: submission.user_id,
                      cover_url: submission.cover_url,
                      // Preserve raw fields so PlayerProvider routes locked
                      // tracks through the preview URL instead of a master.
                      music_url: isSinglesLocked(submission) ? null : musicFileUrl,
                      display_plays: submission.display_plays,
                      singles_for_sale: submission.singles_for_sale,
                      singles_locked: submission.singles_locked,
                      singles_price_usd: submission.singles_price_usd,
                      singles_listing_id: submission.singles_listing_id,
                      singles_buy_url: submission.singles_buy_url,
                      singles_preview_url: submission.singles_preview_url,
                    }}
                    sourceLabel="Track page"
                    size={36}
                    testIdSuffix="track-page-listen-while-browsing"
                  />
                  <span className="text-xs text-purple-200/70">
                    {isSinglesLocked(submission)
                      ? "Preview only — buy the track to unlock the full version."
                      : "Listen while you browse — opens the persistent mini player"}
                  </span>
                </div>
                {submission.music_url_updated_at && (
                  <p
                    className="mt-2 text-[11px] text-purple-300/80"
                    data-testid="audio-updated-at"
                  >
                    🔁 Audio updated: {new Date(submission.music_url_updated_at).toLocaleDateString()}
                  </p>
                )}
                <TrackReactionBar
                  submissionId={submission.submission_id}
                  currentUser={currentUser}
                />
                {/* Phase 7 — TikTok-style virtual gifts. Lives next to the
                    reaction bar (and sibling-not-parent of AudioPlayer) so
                    sending a gift never disturbs playback. */}
                <div className="mt-3" data-testid="track-gift-picker-wrapper">
                  <GiftPicker
                    targetType="track"
                    targetId={submission.submission_id}
                    recipientId={submission.user_id}
                    currentUserId={currentUser?.user_id}
                    currentUserRole={currentUser?.role}
                    onSent={() => setSupportersRefreshKey((k) => k + 1)}
                  />
                </div>
                {/* Phase 7.1 Milestone C — supporter preview row + modal CTA.
                    Lives outside AudioPlayer's subtree so opening the modal
                    never unmounts the player. Refreshes the moment a gift
                    is sent (via the GiftPicker onSent callback above). */}
                <div className="mt-3" data-testid="track-supporters-preview-wrapper">
                  <SupportersPreviewRow
                    kind="track"
                    targetId={submission.submission_id}
                    onViewAll={() => setSupportersOpen(true)}
                    refreshKey={supportersRefreshKey}
                  />
                </div>
              </div>

              {/* Action Buttons — Phase 16.0 mobile-overflow fix.
                  flex-wrap so buttons stack on portrait mobile; horizontal
                  scroll on tiny screens as a fallback; smaller padding +
                  text-xs on mobile so the full row still fits without a
                  page-wide horizontal scrollbar. */}
              <div
                className="flex items-center gap-2 sm:gap-3 mb-6 flex-wrap"
                data-testid="track-actions-row"
              >
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border text-xs sm:text-sm transition-all whitespace-nowrap ${liked ? 'bg-pink-600 border-pink-500 text-white' : 'border-gray-700 text-gray-400 hover:border-pink-500 hover:text-pink-400'}`}
                  data-testid="like-button"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill={liked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span>{likeCount}</span>
                </button>
                {canShare("tracks") && (
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-gray-700 text-gray-400 hover:border-purple-500 hover:text-purple-400 text-xs sm:text-sm transition-all whitespace-nowrap"
                    data-testid="share-button"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span>Share</span>
                  </button>
                )}
                {currentUser && (
                  <button
                    onClick={handleRepost}
                    disabled={repostBusy}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border text-xs sm:text-sm transition-all whitespace-nowrap ${
                      reposted
                        ? "border-emerald-400/60 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20"
                        : "border-purple-500/50 text-purple-300 hover:border-purple-400 hover:text-purple-200 hover:bg-purple-500/10"
                    }`}
                    data-testid="track-repost-btn"
                    title={reposted ? "Reposted to RGM Shuffle" : "Repost to RGM Shuffle"}
                  >
                    <span>🔁</span>
                    <span>{reposted ? "Reposted" : "Repost"}</span>
                  </button>
                )}
                {currentUser && (
                  <button
                    onClick={() => setShareCommunityOpen(true)}
                    className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-purple-500/50 text-purple-300 hover:border-purple-400 hover:text-purple-200 hover:bg-purple-500/10 text-xs sm:text-sm transition-all whitespace-nowrap"
                    data-testid="share-to-community-button"
                    title="Share into your crews, groups, or club"
                  >
                    <span>🤝</span>
                    <span className="hidden xs:inline sm:inline">Share to Community</span>
                    <span className="xs:hidden sm:hidden">Community</span>
                  </button>
                )}
                
                {/* Add to Playlist Button - Only visible to logged in users */}
                {currentUser && (
                  <div className="relative" ref={playlistDropdownRef}>
                    <button
                      onClick={() => setShowPlaylistDropdown(!showPlaylistDropdown)}
                      className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-gray-700 text-gray-400 hover:border-green-500 hover:text-green-400 text-xs sm:text-sm transition-all whitespace-nowrap"
                      data-testid="add-to-playlist-button"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="hidden sm:inline">Add to Playlist</span>
                      <span className="sm:hidden">Playlist</span>
                    </button>
                    
                    {/* Playlist Dropdown */}
                    {showPlaylistDropdown && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-[#1a1a1e] border border-purple-900/30 rounded-lg shadow-xl z-50 overflow-hidden">
                        <div className="p-2 border-b border-purple-900/30">
                          <p className="text-sm text-gray-400">Add to playlist</p>
                        </div>
                        {userPlaylists.length === 0 ? (
                          <div className="p-4 text-center">
                            <p className="text-sm text-gray-400 mb-2">No playlists yet</p>
                            <Link to="/playlists" className="text-sm text-purple-400 hover:text-purple-300">
                              Create a playlist
                            </Link>
                          </div>
                        ) : (
                          <div className="max-h-60 overflow-y-auto">
                            {userPlaylists.map((playlist) => (
                              <button
                                key={playlist.playlist_id}
                                onClick={() => handleAddToPlaylist(playlist.playlist_id)}
                                disabled={addingToPlaylist}
                                className="w-full px-4 py-3 text-left hover:bg-purple-900/20 transition-colors flex items-center gap-3"
                              >
                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                </svg>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm truncate">{playlist.name}</p>
                                  <p className="text-xs text-gray-500">{playlist.track_ids?.length || 0} tracks</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="p-2 border-t border-purple-900/30">
                          <Link
                            to="/playlists"
                            className="block w-full text-center text-sm text-purple-400 hover:text-purple-300 py-2"
                            onClick={() => setShowPlaylistDropdown(false)}
                          >
                            Manage Playlists
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Edit Button - Only visible to track owner */}
                {canEdit && (
                  <Link to={`/track/${submission_id}/edit`}>
                    <button
                      className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-purple-500/50 text-purple-300 hover:bg-purple-500/20 text-xs sm:text-sm transition-all whitespace-nowrap"
                      data-testid="edit-track-button"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Edit</span>
                    </button>
                  </Link>
                )}
              </div>

              {/* Phase 4.0 — AI Track Feedback (private: owner/admin/A&R only) */}
              {(currentUser && (
                currentUser.user_id === submission.user_id ||
                currentUser.role === 'admin' ||
                currentUser.role === 'ar'
              )) && (
                <>
                  <div className="mt-4 flex items-center justify-end">
                    <button
                      onClick={() => setReviewHistoryOpen(true)}
                      className="text-xs px-3 py-1.5 rounded-full border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 hover:text-fuchsia-200 transition-colors flex items-center gap-1.5"
                      data-testid="open-review-history-btn"
                    >
                      <span>📒</span>
                      <span>A&R Review History</span>
                    </button>
                  </div>
                  <AITrackFeedback submissionId={submission.submission_id} />
                  <AIPromoGenerator submissionId={submission.submission_id} />
                </>
              )}

              {/* Phase 4.1 — Collab suggestions tied to this track's genre */}
              <div className="mt-6">
                <CollabSuggestions genre={submission.genre} limit={6} compact />
              </div>
              {/* Description */}
              {submission.description && (
                <div className="mb-6 mt-6">
                  <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-2">About this track</h3>
                  <p className="text-gray-300 leading-relaxed">{submission.description}</p>
                </div>
              )}

              {/* Score Breakdown */}
              {submission.score > 0 && submission.scores && (
                <div className="p-4 bg-black/30 rounded-xl border border-purple-500/20">
                  <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-4">Score Breakdown</h3>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {[
                      { key: 'lyrics', label: 'Lyrics' },
                      { key: 'flow', label: 'Flow' },
                      { key: 'originality', label: 'Originality' },
                      { key: 'mix_quality', label: 'Mix' },
                      { key: 'branding', label: 'Branding' },
                      { key: 'growth_potential', label: 'Growth' }
                    ].map(cat => (
                      <div key={cat.key} className="text-center p-3 bg-purple-900/20 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">{cat.label}</p>
                        <p className={`text-xl font-bold ${(submission.scores[cat.key] || 0) >= 7 ? 'text-green-400' : (submission.scores[cat.key] || 0) >= 5 ? 'text-yellow-400' : 'text-gray-400'}`}>
                          {submission.scores[cat.key] || 0}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* #9 Claim unclaimed milestones (for tracks that crossed
                   thresholds BEFORE this feature shipped). Owner-only UI. */}
              {canEdit && unclaimedMilestones.length > 0 && (
                <div
                  className="mt-6 p-4 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-yellow-500/10"
                  data-testid="claim-milestone-card"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <p className="text-amber-200 font-bold flex items-center gap-2">
                        🎁 Unclaimed milestones
                      </p>
                      <p className="text-xs text-amber-100/80 mt-0.5">
                        Your track already crossed these — claim to celebrate &amp; share.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {unclaimedMilestones.map((m) => (
                      <Button
                        key={m.threshold}
                        size="sm"
                        onClick={() => handleClaimMilestone(m.threshold)}
                        disabled={claimingThreshold === m.threshold}
                        className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold hover:scale-105 transition-transform"
                        data-testid={`claim-milestone-${m.threshold}`}
                      >
                        {claimingThreshold === m.threshold
                          ? 'Claiming…'
                          : <>{m.emoji} Claim {m.threshold.toLocaleString()} plays</>}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Milestone Progress */}
              <div className="mt-6">
                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                  <h3 className="text-gray-400 text-sm uppercase tracking-wider">Play Milestones</h3>
                  {(() => {
                    const top = topMilestoneReached();
                    if (!top) return null;
                    const isHero = [1000, 5000, 10000].includes(top.threshold);
                    return (
                      <button
                        type="button"
                        onClick={() => handleShareMilestone(top.threshold)}
                        data-testid="share-milestone-button"
                        className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all hover:scale-105 shadow-lg ${
                          isHero
                            ? `bg-gradient-to-r ${top.accent} text-black shadow-amber-500/30`
                            : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                        }`}
                      >
                        <span className="text-base">{top.emoji}</span>
                        <span>Share {top.name}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
                          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                          <polyline points="16 6 12 2 8 6"/>
                          <line x1="12" y1="2" x2="12" y2="15"/>
                        </svg>
                      </button>
                    );
                  })()}
                </div>
                <MilestoneBadgeLarge playCount={submission.display_plays ?? submission.play_count ?? 0} showProgress={true} />
              </div>

              {/* Artist Link */}
              <div className="mt-8 pt-6 border-t border-gray-800">
                <Link to={`/artist/${submission.user_id}`}>
                  <Button variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20">
                    View {getArtistName()}'s Profile
                  </Button>
                </Link>
              </div>
            </div>
          </Card>

          {/* Comments Section */}
          <Card className="mt-8 overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-purple-500/30" data-testid="comments-section">
            <div className="p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Comments
                <span className="text-gray-500 font-normal text-sm">({comments.length})</span>
              </h2>

              {/* Comment Input */}
              {currentUser ? (
                <div className="mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={getProfilePhotoUrl(currentUser)}
                        alt={currentUser.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <MentionTextarea
                        value={commentText}
                        onChange={setCommentText}
                        onMentionsChange={setCommentMentions}
                        placeholder="Share your thoughts… type @ to tag someone"
                        maxLength={1000}
                        rows={3}
                        className="bg-black/50 border-gray-700 text-white min-h-[80px]"
                        testIdPrefix="comment"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-500">
                          {commentText.length}/1000
                        </span>
                        <Button
                          onClick={handleSubmitComment}
                          disabled={submittingComment || !commentText.trim()}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                          data-testid="submit-comment-btn"
                        >
                          {submittingComment ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Posting...
                            </>
                          ) : (
                            "Post Comment"
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg text-center">
                  <p className="text-gray-400 mb-3">Log in to leave a comment</p>
                  <Link to="/login">
                    <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500">
                      Log In
                    </Button>
                  </Link>
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No comments yet. Be the first to share your thoughts!
                  </p>
                ) : (
                  // Phase 3.2: nest replies under parents
                  (() => {
                    const top = comments.filter(c => !c.parent_comment_id);
                    const repliesByParent = comments.reduce((acc, c) => {
                      if (c.parent_comment_id) {
                        (acc[c.parent_comment_id] = acc[c.parent_comment_id] || []).push(c);
                      }
                      return acc;
                    }, {});

                    const renderReactions = (comment) => {
                      const r = comment.reactions || {};
                      const entries = Object.entries(r).filter(([, ids]) => Array.isArray(ids) && ids.length > 0);
                      if (entries.length === 0) return null;
                      return (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {entries.map(([emoji, ids]) => {
                            const reacted = currentUser && ids.includes(currentUser.user_id);
                            return (
                              <button
                                key={emoji}
                                onClick={() => handleToggleReaction(comment.comment_id, emoji)}
                                data-testid={`reaction-${comment.comment_id}-${emoji}`}
                                className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 border transition-all ${
                                  reacted ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:bg-gray-700'
                                }`}
                              >
                                <span>{emoji}</span>
                                <span>{ids.length}</span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    };

                    const renderComment = (comment, isReply = false) => (
                      <div
                        key={comment.comment_id}
                        className={`flex items-start gap-3 p-4 ${isReply ? 'ml-10 bg-black/20 border-l-2 border-purple-500/30' : 'bg-black/30'} rounded-lg`}
                        data-testid={`comment-${comment.comment_id}`}
                      >
                        <Link to={`/artist/${comment.user_id}`} className="flex-shrink-0">
                          <div className={`${isReply ? 'w-8 h-8' : 'w-10 h-10'} rounded-full overflow-hidden`}>
                            <img
                              src={getProfilePhotoUrl({
                                profile_photo: comment.user_profile_photo,
                                picture: comment.user_picture,
                              })}
                              alt={comment.user_name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Link to={`/artist/${comment.user_id}`} className="font-medium text-white hover:text-purple-400">
                                {comment.user_name}
                              </Link>
                              <span className="text-gray-600 text-sm">{formatTimeAgo(comment.created_at)}</span>
                            </div>
                            {(currentUser?.user_id === comment.user_id || isAdmin) && (
                              <button
                                onClick={() => handleDeleteComment(comment.comment_id)}
                                className="text-gray-600 hover:text-red-400 transition-colors"
                                title="Delete comment"
                                data-testid={`delete-comment-${comment.comment_id}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <p className="text-gray-300 mt-1 whitespace-pre-wrap break-words">{comment.content}</p>
                          {renderReactions(comment)}
                          <div className="flex items-center gap-3 mt-2 relative">
                            {currentUser && (
                              <>
                                <button
                                  onClick={() => setReactionPickerFor(reactionPickerFor === comment.comment_id ? null : comment.comment_id)}
                                  className="text-gray-400 hover:text-purple-300 text-xs flex items-center gap-1"
                                  data-testid={`react-btn-${comment.comment_id}`}
                                >
                                  <span className="text-sm">😊</span> React
                                </button>
                                {!isReply && (
                                  <button
                                    onClick={() => {
                                      setReplyTargetId(replyTargetId === comment.comment_id ? null : comment.comment_id);
                                      setReplyText("");
                                    }}
                                    className="text-gray-400 hover:text-purple-300 text-xs"
                                    data-testid={`reply-btn-${comment.comment_id}`}
                                  >
                                    Reply
                                  </button>
                                )}
                              </>
                            )}
                            {reactionPickerFor === comment.comment_id && (
                              <div className="absolute z-30 top-7 left-0" data-testid={`emoji-picker-${comment.comment_id}`}>
                                <EmojiPicker
                                  theme={Theme.DARK}
                                  emojiStyle={EmojiStyle.NATIVE}
                                  width={300}
                                  height={350}
                                  onEmojiClick={(emojiData) => handleToggleReaction(comment.comment_id, emojiData.emoji)}
                                />
                              </div>
                            )}
                          </div>
                          {replyTargetId === comment.comment_id && !isReply && (
                            <div className="mt-3 flex flex-col gap-2" data-testid={`reply-form-${comment.comment_id}`}>
                              <MentionTextarea
                                value={replyText}
                                onChange={setReplyText}
                                onMentionsChange={setReplyMentions}
                                placeholder={`Reply to ${comment.user_name}… type @ to tag`}
                                maxLength={1000}
                                rows={2}
                                className="bg-black/50 border-gray-700 text-gray-200 text-sm"
                                testIdPrefix={`reply-${comment.comment_id}`}
                              />
                              <div className="flex items-center gap-2 justify-end">
                                <Button variant="ghost" size="sm" onClick={() => { setReplyTargetId(null); setReplyText(""); }}>
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-purple-600 hover:bg-purple-700"
                                  disabled={!replyText.trim() || submittingReply}
                                  onClick={() => handleSubmitReply(comment.comment_id)}
                                  data-testid={`submit-reply-${comment.comment_id}`}
                                >
                                  {submittingReply ? 'Posting…' : 'Post Reply'}
                                </Button>
                              </div>
                            </div>
                          )}
                          {/* Nested replies */}
                          {!isReply && repliesByParent[comment.comment_id] && (
                            <div className="mt-3 space-y-2">
                              {repliesByParent[comment.comment_id].map(reply => renderComment(reply, true))}
                            </div>
                          )}
                        </div>
                      </div>
                    );

                    return top.map(c => renderComment(c, false));
                  })()
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center justify-center">
            <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </footer>

      {/* Phase 9.7.15 — A&R Review History modal (owner/admin/A&R only) */}
      <ReviewHistoryModal
        submissionId={submission?.submission_id}
        trackTitle={submission?.track_title}
        open={reviewHistoryOpen}
        onOpenChange={setReviewHistoryOpen}
      />

      {/* Phase 9.7.20 — Now Featured share modal */}
      <NowFeaturedShareModal
        open={nowFeaturedShareOpen}
        onOpenChange={setNowFeaturedShareOpen}
        submission={submission}
        artist={{
          artist_name: submission?.artist_display_name || submission?.display_artist_name || submission?.artist_name,
          profile_photo: submission?.user_profile_photo,
          picture: submission?.user_picture,
        }}
      />

      {/* iter_189 — Unified per-track share modal used by the main
          "Share" button (works for ALL tracks, featured or not). */}
      <TrackShareModal
        open={trackShareOpen}
        onClose={() => setTrackShareOpen(false)}
        track={{
          submission_id,
          track_title: submission?.track_title,
          cover_url: submission?.cover_url,
          cover_art: submission?.cover_art,
          cover_updated_at: submission?.cover_updated_at,
          updated_at: submission?.updated_at,
          artist_name: submission?.artist_display_name || submission?.display_artist_name || submission?.artist_name,
        }}
        artist={{
          artist_name: submission?.artist_display_name || submission?.display_artist_name || submission?.artist_name,
          profile_photo: submission?.user_profile_photo || trackData?.artist?.profile_photo,
          picture: submission?.user_picture,
        }}
        currentUser={currentUser}
      />
    </div>
  );
};

export default TrackPage;